import * as z from "zod";
import { suiteApps } from "../enums";
import { liveRoomName, liveUserId } from "./rooms";

// What travels on a socket of Nexxt Live (ADR-276): ids and the request that made the change, never data. The client
// reads again from its gateway, and the 409 of the write stays the guarantee. A client ignores a message it does not
// know, so a new type is a compatible change (ADR-020). The one exception is presence (ADR-279): the names of who
// else is in the room, and of who changed it (ADR-308), filtered for each viewer.

/** The subprotocol of the socket: the browser offers it with the grant (ADR-277), the room answers it alone. */
export const LIVE_PROTOCOL = "nexxt-live.v1";

/** Close codes of the room (4000–4999 are the application's, RFC 6455 §7.4.2). */
export const liveCloseCodes = {
  /**
   * The access of the user changed (`closeUser`), or the grant is older than that change: the client does not open
   * the socket again with the same grant, and asks a new read first (ADR-277).
   */
  revoked: 4403,
  /**
   * The grant the socket was opened with ended (ADR-310): the client reads again for a new one and opens with it, as
   * after a drop. The socket of the user room ends too, so the gateway checks the session again at the next upgrade.
   */
  grantEnded: 4401,
  /**
   * No ping from the client for `SILENT_CLOSE_MS` (ADR-310): a client gone without closing (a laptop asleep, a lost
   * network). A client still there hears it as a drop and opens again.
   */
  silent: 4408,
} as const;

/**
 * How long a grant opens a room, from `issuedAt` (ADR-277): `grantedAt` of the member is that instant, so the room
 * closes the socket at `grantedAt + LIVE_GRANT_TTL_MS` (`grantEnded`, ADR-310). The socket of the user room, opened with
 * the session and no grant, ends as long after the upgrade: the gateway checks the session again when it reopens.
 */
export const LIVE_GRANT_TTL_MS = 15 * 60 * 1000;

/**
 * How often a client pings its socket (ADR-278); the room answers without waking (the auto response) and keeps the
 * time of the last answer, by which it knows a client is still there (ADR-310).
 */
export const LIVE_PING_EVERY_MS = 30_000;

/**
 * After a socket closes at the end of its grant (ADR-310) its page opens again with a new one: the room tells the
 * others it left only this much later, and the client keeps showing who was there about as long, then forgets it if the
 * socket did not come back.
 */
export const LIVE_RENEWAL_GRACE_MS = 10_000;

/**
 * An access checked this soon after a revocation is refused too (ADR-277): the gateway dates the grant and the room
 * dates the revocation, two clocks of two machines. A client that was refused waits longer than this before it reads
 * again for a new grant (ADR-278).
 */
export const REVOCATION_MARGIN_MS = 5_000;

/**
 * Whether an access checked at `grantedAt` opens a socket of a user closed at `revokedAt` (null or undefined: never): a
 * grant given before the revocation, with the Actor of before, opens nothing until it ends (ADR-277, Q-SEC-33). The
 * rule of the rooms of Nexxt Live and of the assistant's (ADR-310).
 */
export function admittedSince(revokedAt: number | null | undefined, grantedAt: number): boolean {
  return revokedAt === undefined || revokedAt === null || grantedAt > revokedAt + REVOCATION_MARGIN_MS;
}

/** The request of the write, as the gateways bound it (`requestContext`). */
const requestId = z.string().max(100);

/** What a user room is told to read again. */
export const liveTopic = z.enum(["notifications"]);

/** Something for this user alone: what `topic` names is to read again. */
const notifiedMessage = z.object({ type: z.literal("notified"), topic: liveTopic, requestId });

/**
 * A row of a screen a person is on (ADR-279): the id the grid gives the row (`getRowId`), never what it shows.
 */
export const liveRowId = z.string().regex(/^[A-Za-z0-9_.:-]{1,100}$/);

/** At most this many people in a presence message: beyond them `more` counts them. */
export const MAX_PRESENT = 20;
/** At most this many rows of one person (their tabs) in a presence message. */
export const MAX_PRESENCE_ROWS = 10;
/** How long a viewer shows "is typing" after the last `typing` it heard of: the typer says it again meanwhile. */
export const TYPING_MS = 5_000;
/** A client says `typing` at most this often while its user types; the room takes it at most twice as often. */
export const TYPING_EVERY_MS = 2_000;

/**
 * Somebody else in the room, as this viewer may see them (ADR-279, B-131): a person by name, with their company, or
 * a company alone, when the viewer may not know who of it is there ("Tomorrow" for an external user).
 */
export const presentPerson = z.object({
  /** Stable while they stay: a user id, or the company's key for a company alone. */
  key: z.string().max(100),
  name: z.string().max(120),
  /** The company of a person; null when `name` already is the company. */
  organization: z.string().max(120).nullable(),
  /** The rows they are on, one per tab at most. */
  rows: z.array(liveRowId).max(MAX_PRESENCE_ROWS),
  typing: z.boolean(),
  /**
   * A delegated access that just wrote here (ADR-300 §7): `name` is then the label of its kind (`Claude`), never a
   * name its client chose, and this is who delegated it, as the viewer may see them. Absent for a person; a client of
   * before shows only the label.
   */
  delegatedBy: z.string().max(120).optional(),
});
export type PresentPerson = z.output<typeof presentPerson>;

/**
 * Something in the room changed: read it again, unless `requestId` is the client's own write. `by`: who changed it, as
 * this viewer may see them (ADR-308, the rule of the presence, B-131), only to a socket whose screen shows who is here
 * and only when the room knows the writer (a person with a socket here, a delegated access, Tomorrow for an external
 * viewer); never the viewer themselves. A client of before ignores it (ADR-020).
 */
const changedMessage = z.object({
  type: z.literal("changed"),
  room: liveRoomName,
  requestId,
  by: presentPerson.pick({ key: true, name: true, organization: true, delegatedBy: true }).optional(),
});

/**
 * Who else is in the room, for this socket alone: each socket gets the list its viewer may see (ADR-279). With `by` of
 * `changed`, the messages with names, sent only to a socket whose screen said it shows who is here (`here`).
 */
const presenceMessage = z.object({
  type: z.literal("presence"),
  room: liveRoomName,
  present: z.array(presentPerson).max(MAX_PRESENT),
  more: z.number().int().nonnegative(),
});

/** What a client reads off its socket (ADR-278): anything else, a type of a later version too, it ignores. */
export const liveMessage = z.discriminatedUnion("type", [changedMessage, notifiedMessage, presenceMessage]);
export type LiveMessage = z.output<typeof liveMessage>;
/** Who changed something in a room, as one viewer may see them (`by` of `changed`, ADR-308). */
export type LiveChangedBy = NonNullable<z.output<typeof changedMessage>["by"]>;

/**
 * What a client says on its socket besides the ping (ADR-279), only in the room of an aggregate:
 * - `here`: its screen shows who is here, and the user is on `row` (null: on none);
 * - `typing`: the user is typing a note;
 * - `away`: no screen of it shows who is here any more.
 * A room ignores anything else, and anything from a socket it has not admitted yet.
 */
export const liveClientMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("here"), row: liveRowId.nullable() }),
  z.object({ type: z.literal("typing") }),
  z.object({ type: z.literal("away") }),
]);
export type LiveClientMessage = z.output<typeof liveClientMessage>;

/**
 * Who a person is to the others in a room (ADR-279): what the gateway read of them when it checked the access, and
 * the room filters for each viewer (B-131). Never read from the browser.
 */
export const livePresenceProfile = z.object({
  name: z.string().min(1).max(120),
  organizationId: z.uuid(),
  organizationName: z.string().min(1).max(120),
  internal: z.boolean(),
});
export type LivePresenceProfile = z.output<typeof livePresenceProfile>;

/**
 * Who opens a socket, as the gateway puts it after checking the grant (ADR-277): the room never reads it from the
 * browser. JSON in the `x-nexxt-live-member` header of the upgrade. `grantedAt`: when the access was checked, i.e.
 * when the request that gave the grant arrived at the gateway, before its Actor was built (epoch ms); a user room
 * refuses what was granted before it closed its user (`closeUser`). `profile`: in the room of an aggregate, unless
 * the gateway could not read it; without it the person is not shown, and sees nobody (ADR-279).
 */
export const liveMember = z.object({
  userId: liveUserId,
  grantedAt: z.number().int().nonnegative(),
  profile: livePresenceProfile.optional(),
});
export type LiveMember = z.output<typeof liveMember>;

export const LIVE_MEMBER_HEADER = "x-nexxt-live-member";
/** The room the upgrade is for: a Durable Object does not know the name it was reached by. */
export const LIVE_ROOM_HEADER = "x-nexxt-live-room";

/**
 * The grant of a room, on the response of the read that authorized it (ADR-277): the browser offers it back as the
 * subprotocol `grant.<grant>`, next to `LIVE_PROTOCOL`, when it opens `/live/rooms/<room>`.
 */
export const LIVE_GRANT_HEADER = "x-nexxt-live-grant";
export const LIVE_GRANT_PROTOCOL_PREFIX = "grant.";

/**
 * What a grant says, the middle part of `<version>.<claims>.<signature>` in base64url (ADR-277): the gateway signs and
 * checks it, the browser reads the room and the end to know which socket it opens and until when (ADR-278).
 */
export const liveGrantClaims = z.object({
  app: z.enum(suiteApps),
  room: liveRoomName,
  userId: liveUserId,
  /** When the request that gave it arrived, before its Actor was built (epoch ms): `receivedAt`. */
  issuedAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
});
export type LiveGrantClaims = z.output<typeof liveGrantClaims>;

/**
 * Why a gateway refused an upgrade (ADR-277). A browser sees only that the socket did not open: the codes are for the
 * logs, the access log and the tests.
 */
export type LiveErrorCode =
  /** `LIVE` is off, or the gateway has no key for the grants: the screens poll as before. */
  | "live_unavailable"
  | "upgrade_required"
  | "live_origin_rejected"
  /** Not a person signed in to an app: a machine token, a delegated access. */
  | "live_forbidden"
  | "live_grant_invalid";
